import React, { useEffect } from 'react';
import { FaPlus as FaPlusSolid } from 'react-icons/fa';

import { Popover, Tooltip } from 'antd';
import AddNewButton from 'components/common/Button/AddNewButton';
import { TASKS_GRID_ADD_BUTTON_LABEL } from 'constants/uiConstants';
import type { IInputTask } from 'interfaces/IInputTask';

import { StepSelection } from './stepFactory';
import StepInputSelector from './StepInputSelector';
import { TASK_ADD_INPUT_ATTR } from './taskEntryFocus';
import {
  getAddCategoryKeysForTask,
  getAddLockedCategoryReasonsForTask,
} from './yieldLinkedCosts';

interface AddInputControlProps {
  task: IInputTask;
  addInputCategoryKey?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onSelect: (selections: StepSelection[]) => void;
  enableMultiSelect?: boolean;
  /** Disables the button and explains why in a tooltip. */
  disabledReason?: string;
  /** Button text; "Add" by default. */
  label?: string;
  /**
   * A green filled circle behind the + (the app's AddNewButton pattern), so
   * the task's own "+ Add input" stands out in the sheet (OP-2556).
   */
  hasFilledIcon?: boolean;
}

// The per-task "+ Add" button that opens the input (step) picker — shared by
// the step sub-table's footer (add another input) and, via the same picker,
// every step's own "change input" popover.
const AddInputControl: React.FC<AddInputControlProps> = ({
  task,
  addInputCategoryKey,
  isOpen,
  onOpenChange,
  onClose,
  onSelect,
  enableMultiSelect = true,
  disabledReason,
  label = TASKS_GRID_ADD_BUTTON_LABEL,
  hasFilledIcon = false,
}) => {
  // A Production task offers its product and Operation costs; once it has
  // its product, Production stays in view, disabled with why (OP-2399).
  const categoryKeys = getAddCategoryKeysForTask(task);
  const lockedCategoryReasons = getAddLockedCategoryReasonsForTask(task);
  const isUsable = (key: string) =>
    (!categoryKeys || categoryKeys.includes(key)) &&
    !lockedCategoryReasons?.[key];
  const initialCategoryKey =
    addInputCategoryKey && isUsable(addInputCategoryKey)
      ? addInputCategoryKey
      : (categoryKeys?.find(isUsable) ?? addInputCategoryKey);

  useEffect(() => {
    if (disabledReason && isOpen) onOpenChange(false);
  }, [disabledReason, isOpen, onOpenChange]);

  return (
    <Popover
      trigger="click"
      placement="right"
      autoAdjustOverflow
      destroyOnHidden
      getPopupContainer={() => document.body}
      open={isOpen && !disabledReason}
      onOpenChange={(open) => {
        if (open && disabledReason) return;
        onOpenChange(open);
      }}
      classNames={{
        root: 'step-input-selector-popover',
      }}
      content={
        <StepInputSelector
          taskType={task.taskType}
          initialCategoryKey={initialCategoryKey}
          categoryKeys={categoryKeys}
          lockedCategoryReasons={lockedCategoryReasons}
          onClose={onClose}
          onSelect={onSelect}
          enableMultiSelect={enableMultiSelect}
          enableAddRecord
          task={task}
        />
      }
    >
      <Tooltip title={disabledReason}>
        <span {...{ [TASK_ADD_INPUT_ATTR]: task.taskId }}>
          <AddNewButton
            text={label}
            size="sm"
            disabled={!!disabledReason}
            className="gap-1 border border-solid border-gray-argent bg-white px-2 py-1 text-xs font-semibold text-secondary-dark hover:border-gray-argent hover:bg-gray-bright"
            icon={
              hasFilledIcon ? (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-success">
                  <FaPlusSolid className="text-white" size={8} />
                </span>
              ) : (
                <FaPlusSolid className="text-secondary-dark" size={10} />
              )
            }
          />
        </span>
      </Tooltip>
    </Popover>
  );
};

export default AddInputControl;
